import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { MicIcon } from '../assets/MicIcon';
import { StopIcon } from '../assets/StopIcon';
import { MicMeterDisp } from './MicMeterDisp';
import { blueEnabledColor, greyActiveColor, lightGrey, veryVeryLightGrey, white } from '../styles/colors';

/**
 * SpeechButton is the microphone button and the volume level display, together.
 *   It will know its own size based on buttonSize prop and whether there is speakText prop
 */

var MIN_TEXT_WIDTH = 112;
var TEXT_OFFSET = 4;
var DISABLED_MIC_BLUE = '#bbdaef';

var getFontSize = function getFontSize(buttonSize) {
  return buttonSize > 40 ? 14 : 12;
};
var getLineHeight = function getLineHeight(buttonSize) {
  return buttonSize > 40 ? 21 : 18;
};

var SpeechButtonDiv = styled.div(function (props) {
  var style = {
    position: 'relative',
    width: props.buttonSize,
    height: props.buttonSize,
    cursor: 'pointer'
  };
  if (props.disabled) {
    style.pointerEvents = 'none';
  }
  if (props.hasText) {
    style.width = MIN_TEXT_WIDTH;
    style.height += getLineHeight(props.buttonSize) + TEXT_OFFSET;
  }
  return style;
});

var SpeechButtonTextDiv = styled.div(function (props) {
  var style = {
    position: 'relative',
    top: props.buttonSize + TEXT_OFFSET,
    width: '100%',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    fontSize: getFontSize(props.buttonSize),
    lineHeight: getLineHeight(props.buttonSize) + 'px',
    color: blueEnabledColor
  };
  if (props.disabled) {
    style.color = lightGrey;
  } else if (props.labelColor) {
    style.color = props.labelColor;
  }
  return style;
});

var MicIconDiv = styled.div(function (props) {
  var iconLeft = 0;
  if (props.hasText) {
    iconLeft = (MIN_TEXT_WIDTH - props.buttonSize) / 2;
  }
  var style = {
    position: 'absolute',
    top: 0,
    left: iconLeft,
    width: props.buttonSize,
    height: props.buttonSize,
    zIndex: 2
  };
  return style;
});

var MicLevelPositioner = styled(MicIconDiv)({
  zIndex: 1
});

//***************************************************************************************
export var SpeechButton = function (_PureComponent) {
  _inherits(SpeechButton, _PureComponent);

  function SpeechButton() {
    _classCallCheck(this, SpeechButton);

    return _possibleConstructorReturn(this, _PureComponent.apply(this, arguments));
  }

  SpeechButton.prototype.componentWillUnmount = function componentWillUnmount() {
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  };

  SpeechButton.prototype.render = function render() {
    var textDisp = void 0;
    if (!!this.props.speakText) {
      // if speech button has speakText, textDisp should be rendered even if
      //  there is no stopText, to avoid element size changing
      var buttonText = this.props.speakText;
      if (this.props.isListening) {
        buttonText = this.props.stopText;
      }
      textDisp = React.createElement(
        SpeechButtonTextDiv,
        {
          disabled: this.props.disabled,
          buttonSize: this.props.buttonSize,
          labelColor: this.props.labelColor
        },
        buttonText
      );
    }

    var iconDisp = void 0;
    if (this.props.isListening) {
      if (!this.props.isStoppable) {
        // listening but button is not allowing stop, background is blue but
        //  mic looks kinda disabled
        iconDisp = React.createElement(MicIcon, { circleColor: blueEnabledColor, iconColor: DISABLED_MIC_BLUE });
      } else {
        // listening and we can stop, background is blue and icon is white
        iconDisp = React.createElement(StopIcon, { circleColor: blueEnabledColor, iconColor: white });
      }
    } else {
      if (this.props.disabled) {
        iconDisp = React.createElement(MicIcon, { circleColor: veryVeryLightGrey, iconColor: lightGrey });
      } else {
        // not recording/listening, enabled
        iconDisp = React.createElement(MicIcon, { circleColor: greyActiveColor, iconColor: blueEnabledColor });
      }
    }

    var micLevelDisp = void 0;
    if (this.props.isListening) {
      micLevelDisp = React.createElement(
        MicLevelPositioner,
        { hasText: !!textDisp, buttonSize: this.props.buttonSize },
        React.createElement(MicMeterDisp, null)
      );
    }
    return React.createElement(
      SpeechButtonDiv,
      {
        'data-qa': 'SpeechButton',
        onClick: this.props.onClick,
        buttonSize: this.props.buttonSize,
        disabled: this.props.disabled || this.props.isListening && !this.props.isStoppable,
        hasText: !!textDisp
      },
      micLevelDisp,
      React.createElement(
        MicIconDiv,
        { hasText: !!textDisp, buttonSize: this.props.buttonSize },
        iconDisp
      ),
      textDisp
    );
  };

  return SpeechButton;
}(PureComponent);
SpeechButton.propTypes = {
  buttonSize: PropTypes.number.isRequired,
  labelColor: PropTypes.string,
  disabled: PropTypes.bool,
  onUnmount: PropTypes.func,
  onClick: PropTypes.func.isRequired,
  isListening: PropTypes.bool.isRequired,
  isStoppable: PropTypes.bool.isRequired,
  speakText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  stopText: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};